import {RefObject, useCallback, useLayoutEffect, useRef} from 'react';

import {DROP_MENU_SELECTOR} from 'common/components/drop-menu/types';
import {ESCAPE_KEY_CODE, eventWasOutside, rafRetry, TAB_KEY_CODE} from 'common/util/dom';
import {getActiveElement, getFocusableChildren} from 'common/util/focusable';

import {
  deleteCurrentStackingContextTarget,
  getCurrentStackingContextDepth,
  setCurrentStackingContextTarget,
  STACKABLE_ELEMENTS_SELECTOR,
  Visibility,
  VisibilityEvents,
} from './stacking-context';
import {StackableEvent, useStackable} from './use-stackable';

interface UseStackableDismissalOptions {
  /** Whether the surface is showing. Each change opens or closes it in the shared stack. */
  open: boolean;
  /**
   * The element that contains the surface's content. It dispatches the `stackable-*` events, and a
   * mousedown whose composed path passes through it is inside the surface. It must match
   * `STACKABLE_ELEMENTS_SELECTOR`, for example with `data-drop-menu` or `data-stackable-context`, so
   * that it counts toward its own depth and compares equal to a peer at the same nesting.
   */
  hostRef: RefObject<HTMLElement | null>;
  /**
   * Called after a peer opening, an outside mousedown or Escape has already closed the surface in
   * the stack. The consumer must respond by setting `open` to false.
   */
  onDismiss: () => void;
  /**
   * When false, the surface stays out of the stack: it dispatches no `stackable-*` events, and peers,
   * outside mousedowns and Escape leave it open. Autofocus still applies. Changing it while open joins
   * or leaves the stack, with the matching `stackable-open` or `stackable-closed`.
   */
  joinStack?: boolean;
  /** Keep the surface open when a peer opens. Outside mousedowns and Escape still dismiss it. */
  preventClose?: boolean;
  /** A mousedown whose composed path matches any of these selectors is not outside the surface. */
  preventCloseSelectors?: string[];
  /** Focus the first focusable descendant of the host on open. */
  autoFocus?: boolean;
  /** Cycle Tab and Shift+Tab through the host's focusable descendants while this is the innermost surface. */
  trapTab?: boolean;
}

// Counts every stackable element along the path, so a surface's own depth and a peer's depth compare
// like for like.
function countStackingElements(path: EventTarget[]): number {
  return path.filter((target) => target instanceof Element && target.matches(STACKABLE_ELEMENTS_SELECTOR)).length;
}

// Hosts of the hook surfaces currently in the shared stack, in the order they joined it.
const joinedHosts = new Map<string, HTMLElement>();

// Escape and outside-mousedown events that have already dismissed a hook surface.
const dismissingEvents = new WeakSet<Event>();

/**
 * Whether `event` has already dismissed a hook surface. Overlays that dismiss through another
 * mechanism, such as Base UI's outside-press, consult this so one press closes at most one surface.
 */
export function isStackableDismissalEvent(event: Event | undefined): boolean {
  return event !== undefined && dismissingEvents.has(event);
}

function isComposedDescendant(node: Element, ancestor: Element): boolean {
  let current: Element | null = node;
  while (current) {
    const root = current.getRootNode();
    current = current.parentElement ?? (root instanceof ShadowRoot ? root.host : null);
    if (current === ancestor) {
      return true;
    }
  }
  return false;
}

/**
 * React runs layout effects child first, so when nested surfaces open in one commit, the inner one
 * joins the stack before its ancestor. Re-append the hook surfaces nested inside `host` so they stay
 * innermost, as they would have had they opened after it.
 */
function moveNestedSurfacesAbove(stackId: string, host: HTMLElement) {
  for (const [nestedId, nestedHost] of Array.from(joinedHosts)) {
    if (nestedId !== stackId && isComposedDescendant(nestedHost, host)) {
      deleteCurrentStackingContextTarget(nestedId);
      setCurrentStackingContextTarget(nestedId);
      joinedHosts.delete(nestedId);
      joinedHosts.set(nestedId, nestedHost);
    }
  }
}

function getFocusableDescendants(host: HTMLElement | null): HTMLElement[] {
  if (!host) {
    return [];
  }
  const descendants = getFocusableChildren(host);
  return getFocusableChildren(host.shadowRoot, new Set(descendants));
}

function focusNextDescendantOnTab(host: HTMLElement | null, ev: KeyboardEvent) {
  const descendants = getFocusableDescendants(host);
  // With nothing to focus, keep Tab from leaving the surface for the rest of the document.
  if (!descendants.length) {
    ev.preventDefault();
    return;
  }
  const direction = ev.shiftKey ? -1 : 1;
  const focusedIndex = descendants.indexOf(getActiveElement(document) as HTMLElement);
  ev.preventDefault();
  if (focusedIndex === -1) {
    descendants[0].focus();
  } else {
    const nextIndex = (focusedIndex + direction) % descendants.length;
    descendants[nextIndex < 0 ? descendants.length - 1 : nextIndex].focus();
  }
}

/**
 * The dismissal half of the stacking-context protocol for a React surface. `useStackable` supplies
 * membership and the `stackable-*` events; this hook adds, while the surface is open:
 *
 * - Peer close: another stackable opening at the same depth closes this one when it is a drop menu,
 *   or when this surface is the only open stackable. Depths are counted along each `stackable-open`
 *   event's composed path, so a menu nested inside this one leaves it open.
 * - Outside mousedown (capture phase) and Escape, only while this surface is the innermost open
 *   stackable. Escape is matched by `keyCode`.
 * - Autofocus of the first focusable descendant, and optional Tab trapping.
 *
 * A dismissal leaves the stack synchronously, inside the event that caused it, before `onDismiss`
 * runs. For a peer close that means this surface's `stackable-closed` reports the depth from before
 * the peer joined. One Escape or outside mousedown dismisses at most one
 * hook surface, even when that dismissal makes another one innermost while the event is dispatching.
 *
 * When nested hook surfaces open in the same commit, the ancestor moves them back above itself as it
 * joins, so the innermost one is dismissed first.
 *
 * The open transition runs in a layout effect, so a replaced peer never paints alongside its
 * replacement. An unmount while open dispatches `stackable-closed` from the still-connected host
 * before leaving the stack.
 */
export function useStackableDismissal({
  open,
  hostRef,
  onDismiss,
  joinStack = true,
  preventClose = false,
  preventCloseSelectors = [],
  autoFocus = true,
  trapTab = false,
}: UseStackableDismissalOptions): {stackId: string} {
  const {stackId, notifyOpen, notifyClosed, release, isCurrentTarget} = useStackable();

  // Read through a ref so the listeners see the latest props without re-subscribing.
  const optionsRef = useRef({onDismiss, preventClose, preventCloseSelectors, autoFocus, trapTab});
  optionsRef.current = {onDismiss, preventClose, preventCloseSelectors, autoFocus, trapTab};

  const visibleRef = useRef(false);
  // Set while this surface is in the shared stack; calling it leaves the stack and stops listening.
  const leaveStackRef = useRef<(() => void) | null>(null);

  const leaveStack = useCallback(() => {
    const leave = leaveStackRef.current;
    leaveStackRef.current = null;
    leave?.();
  }, []);

  const enterStack = useCallback(() => {
    if (leaveStackRef.current) {
      return;
    }

    const dismiss = (ev: Event) => {
      dismissingEvents.add(ev);
      leaveStack();
      visibleRef.current = false;
      optionsRef.current.onDismiss();
    };

    // Set from this surface's own `stackable-open` as it reaches the window; it stays null when the
    // host was missing at open, which leaves this surface open whatever its peers do.
    let ownDepth: number | null = null;

    const onStackableOpen = (ev: Event) => {
      const path = ev.composedPath();
      const depth = countStackingElements(path);
      if ((ev as StackableEvent).detail?.target === stackId) {
        ownDepth = depth;
        return;
      }
      const opener = path[0];
      const openerIsDropMenu = opener instanceof Element && opener.matches(DROP_MENU_SELECTOR);
      if (
        !optionsRef.current.preventClose &&
        depth === ownDepth &&
        (getCurrentStackingContextDepth() === 1 || openerIsDropMenu)
      ) {
        dismiss(ev);
      }
    };

    const onMousedown = (ev: MouseEvent) => {
      const host = hostRef.current;
      if (
        host &&
        !dismissingEvents.has(ev) &&
        isCurrentTarget() &&
        eventWasOutside(ev, host, optionsRef.current.preventCloseSelectors)
      ) {
        dismiss(ev);
      }
    };

    const onKeydown = (ev: KeyboardEvent) => {
      if (dismissingEvents.has(ev) || !isCurrentTarget()) {
        return;
      }
      if (ev.keyCode === ESCAPE_KEY_CODE) {
        dismiss(ev);
      } else if (ev.keyCode === TAB_KEY_CODE && optionsRef.current.trapTab) {
        focusNextDescendantOnTab(hostRef.current, ev);
      }
    };

    // Listening before the dispatch is what lets this surface measure its own depth.
    window.addEventListener(VisibilityEvents[Visibility.Open], onStackableOpen);
    const host = hostRef.current;
    if (host && !host.matches(STACKABLE_ELEMENTS_SELECTOR)) {
      console.warn(
        `useStackableDismissal: the host does not match STACKABLE_ELEMENTS_SELECTOR, so peers at the same nesting will not dismiss it`,
        host,
      );
    }
    notifyOpen(host);
    if (host) {
      joinedHosts.set(stackId, host);
      moveNestedSurfacesAbove(stackId, host);
    }
    window.addEventListener(`mousedown`, onMousedown, true);
    document.addEventListener(`keydown`, onKeydown);

    leaveStackRef.current = () => {
      notifyClosed(hostRef.current);
      joinedHosts.delete(stackId);
      window.removeEventListener(VisibilityEvents[Visibility.Open], onStackableOpen);
      window.removeEventListener(`mousedown`, onMousedown, true);
      document.removeEventListener(`keydown`, onKeydown);
    };
  }, [hostRef, isCurrentTarget, leaveStack, notifyClosed, notifyOpen, stackId]);

  useLayoutEffect(() => {
    if (!open) {
      visibleRef.current = false;
      leaveStack();
      return;
    }
    const opening = !visibleRef.current;
    visibleRef.current = true;
    if (joinStack) {
      enterStack();
    } else {
      leaveStack();
    }
    if (opening && optionsRef.current.autoFocus) {
      void rafRetry(() => {
        if (!visibleRef.current) {
          return true;
        }
        const firstDescendant = getFocusableDescendants(hostRef.current)[0];
        firstDescendant?.focus();
        return Boolean(firstDescendant);
      });
    }
  }, [open, joinStack, enterStack, leaveStack, hostRef]);

  useLayoutEffect(
    () => () => {
      leaveStack();
      release();
    },
    [leaveStack, release],
  );

  return {stackId};
}
